/**
 * @fileoverview useSessionCardState Hook
 * 
 * Custom hook that manages all state and side effects for the SessionCard component.
 * Handles local state, store subscriptions, and event handlers.
 * 
 * @module vtc/components/SessionCard/useSessionCardState
 */

import { useState, useEffect, useCallback } from 'react';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';

/**
 * useSessionCardState Hook
 * 
 * Manages all state and logic for a SessionCard component including:
 * - Local UI state (loading, notifications, focus)
 * - Store subscriptions (session, app)
 * - Event handlers for SessionViewer events
 * - Memoized SessionViewer instance
 * 
 * @param {string} sessionSid - Unique session identifier
 * @returns {Object} State and handlers for SessionCard
 * @returns {boolean} return.isFocused - Whether this session is focused
 * @returns {boolean} return.allowFocusOption - Whether focus button is enabled
 * @returns {number} return.unReadMessageCount - Count of unread messages
 * @returns {number} return.flagCount - Count of flags
 * @returns {boolean} return.showLoader - Whether to show loading overlay
 * @returns {string} return.loaderMessage - Message for loading overlay
 * @returns {JSX.Element} return.cardViewer - Memoized SessionViewer component
 * @returns {Function} return.handleTransfer - Transfer session handler
 * @returns {Function} return.handleEndSession - End session handler
 * @returns {Function} return.handleOpenChat - Open chat handler
 * @returns {Function} return.handleFocus - Focus session handler
 * @returns {Function} return.handleUnreadMessageCountChange - Unread count change handler
 * @returns {Function} return.handleFlagCountChange - Flag count change handler
 * @returns {Function} return.handleSessionLoaded - Session loaded handler
 * @returns {Function} return.handleOnConnected - Connection established handler
 * @returns {Function} return.handleDisconnected - Disconnection handler
 */
export function useSessionCardState(sessionSid) {
  // ============================================================================
  // STORE SUBSCRIPTIONS
  // ============================================================================

  // Use selective subscriptions to prevent unnecessary re-renders
  const focusedSession = useSessionStore((state) => state.focusedSession);
  const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);
  const focusedSessionLocation = useSessionStore(
    (state) => state.focusedSession?.location
  );

  // Actions don't cause re-renders, safe to get from store
  const setTransferSessionSid = useSessionStore((state) => state.setTransferSessionSid);
  const setEndSessionSid = useSessionStore((state) => state.setEndSessionSid);

  const openDrawer = useAppStore((state) => state.openDrawer);
  const allowSounds = useAppStore((state) => state.allowSounds);
  const magnification = useAppStore((state) => state.magnification);

  // ============================================================================
  // LOCAL STATE
  // ============================================================================

  const [allowFocusOption, setAllowFocusOption] = useState(true);
  const [unReadMessageCount, setUnreadMessageCount] = useState(0);
  const [flagCount, setFlagCount] = useState(0);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const isFocused = focusedSessionSid === sessionSid;

  // The viewer is now rendered via SessionViewerContext
  // We just return the sessionSid for the portal
  const cardViewer = sessionSid;

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Initial loading state
   * Shows loader on component mount until session is loaded
   */
  useEffect(() => {
    if (!sessionLoaded) {
      setLoaderMessage('Loading...');
      setShowLoader(true);
    }
  }, [sessionLoaded]);

  /**
   * Focus option availability
   * Disables focus button when another session is in review state
   */
  useEffect(() => {
    if (focusedSession?.location === 'review') {
      setAllowFocusOption(false);
    } else {
      setAllowFocusOption(true);
    }
  }, [focusedSessionLocation, focusedSession]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles unread message count changes
   * Plays sound notification if enabled
   */
  const handleUnreadMessageCountChange = useCallback((count) => {
    if (count > 0 && allowSounds) {
      const audio = new Audio('/assets/sounds/message-received.mp3');
      audio.play();
    }
    setUnreadMessageCount(count);
  }, [allowSounds]);

  /**
   * Handles flag count changes
   */
  const handleFlagCountChange = useCallback((count) => {
    setFlagCount(count);
  }, []);

  /**
   * Handles session loaded event
   */
  const handleSessionLoaded = useCallback(() => {
    setSessionLoaded(true);
    setShowLoader(false);
  }, []);

  /**
   * Handles media connection established
   */
  const handleOnConnected = useCallback((data) => {
    console.log('Session connected:', data);
    setShowLoader(false);
  }, []);

  /**
   * Handles media disconnection
   */
  const handleDisconnected = useCallback(() => {
    setLoaderMessage('Attempting Reconnection');
    setShowLoader(true);
  }, []);

  /**
   * Handles transfer action
   */
  const handleTransfer = useCallback(() => {
    setTransferSessionSid(sessionSid);
  }, [sessionSid, setTransferSessionSid]);

  /**
   * Handles end session action
   */
  const handleEndSession = useCallback(() => {
    setEndSessionSid(sessionSid);
  }, [sessionSid, setEndSessionSid]);

  /**
   * Handles open chat action
   */
  const handleOpenChat = useCallback(() => {
    openDrawer('focus', sessionSid);
  }, [sessionSid, openDrawer]);

  /**
   * Handles focus action
   */
  const handleFocus = useCallback(() => {
    openDrawer('focus', sessionSid);
  }, [sessionSid, openDrawer]);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    sessionSid,
    isFocused,
    allowFocusOption,
    unReadMessageCount,
    flagCount,
    showLoader,
    loaderMessage,
    cardViewer,
    // Handlers
    handleTransfer,
    handleEndSession,
    handleOpenChat,
    handleFocus,
    handleUnreadMessageCountChange,
    handleFlagCountChange,
    handleSessionLoaded,
    handleOnConnected,
    handleDisconnected,
  };
}
